import { z } from 'zod';
/**
 * Каталог прав (ТЗ §7.2). Плоский список строк вида `ресурс:действие[:область]`.
 *
 * Список закрытый: право, которого здесь нет, не существует. Это позволяет
 * проверять на старте, что каждый эндпоинт требует существующее право,
 * а не строку с опечаткой, которая молча никому не выдана.
 */
export const Permission = {
    SITE_READ: 'site:read',
    SITE_WRITE: 'site:write',
    NODE_READ: 'node:read',
    NODE_WRITE: 'node:write',
    MONITOR_READ: 'monitor:read',
    MONITOR_WRITE: 'monitor:write',
    VNC_CONNECT: 'vnc:connect',
    VNC_FILETRANSFER_READ: 'vnc:filetransfer:read',
    VNC_FILETRANSFER_WRITE: 'vnc:filetransfer:write',
    SSH_CONNECT: 'ssh:connect',
    SSH_SFTP_READ: 'ssh:sftp:read',
    SSH_SFTP_WRITE: 'ssh:sftp:write',
    CAMERA_VIEW: 'camera:view',
    CAMERA_ARCHIVE: 'camera:archive',
    CREDENTIAL_WRITE: 'credential:write',
    AUDIT_READ: 'audit:read',
    ORG_ADMIN: 'org:admin',
};
export const ALL_PERMISSIONS = Object.values(Permission);
export const permissionSchema = z.enum(Object.values(Permission));
export function isPermission(value) {
    return ALL_PERMISSIONS.includes(value);
}
/**
 * Системные роли по умолчанию (ТЗ §11, фаза 1, задача 5).
 *
 * Создаются при инициализации организации. `org_id` у них NULL — они общие
 * для всех организаций, но выданные права действуют только внутри своей.
 */
export const SYSTEM_ROLES = {
    ADMIN: 'Администратор',
    ENGINEER: 'Инженер',
    OBSERVER: 'Наблюдатель',
};
/** Инженер — всё, кроме администрирования организации и записи учётных данных. */
const ENGINEER_PERMISSIONS = ALL_PERMISSIONS.filter((p) => p !== Permission.ORG_ADMIN && p !== Permission.CREDENTIAL_WRITE);
/**
 * Права, которые Наблюдателю не положены, несмотря на окончание `:read`.
 *
 * Журнал аудита содержит персональные данные — кто, когда и откуда работал.
 * Заказчик решил, что роли с наименьшими правами он не полагается (ADR 008).
 *
 * Чтение файлов — не чтение конфигурации платформы, а выгрузка содержимого
 * чужих машин: `vnc:filetransfer:read` и `ssh:sftp:read` позволяют скачать
 * что угодно с рабочего стола или сервера. В набор Наблюдателя они попали
 * бы автоматически по окончанию `:read` — это следствие фильтра, а не
 * решение (решение заказчика от 29.08.2026: не давать).
 */
const OBSERVER_EXCLUDED = [
    Permission.AUDIT_READ,
    Permission.VNC_FILETRANSFER_READ,
    Permission.SSH_SFTP_READ,
];
/**
 * Наблюдатель — чтение и просмотр видео, без журнала аудита.
 *
 * Собирается фильтром по каталогу, а не перечислением: при добавлении нового
 * права на запись Наблюдатель его не получит, а новое право на чтение получит
 * автоматически. Исключения перечислены явно и видны в одном месте.
 */
const OBSERVER_PERMISSIONS = ALL_PERMISSIONS.filter((p) => (p.endsWith(':read') || p === Permission.CAMERA_VIEW) && !OBSERVER_EXCLUDED.includes(p));
export const SYSTEM_ROLE_PERMISSIONS = {
    [SYSTEM_ROLES.ADMIN]: ALL_PERMISSIONS,
    [SYSTEM_ROLES.ENGINEER]: ENGINEER_PERMISSIONS,
    [SYSTEM_ROLES.OBSERVER]: OBSERVER_PERMISSIONS,
};
/**
 * Итоговые права пользователя — объединение ролевых (ТЗ §10.3).
 * Точечные права на узел добавляются к ним `withNodePermissions`.
 */
export function mergePermissions(roles) {
    const result = new Set();
    for (const role of roles) {
        for (const permission of role.permissions) {
            if (isPermission(permission)) {
                result.add(permission);
            }
        }
    }
    return [...result];
}
/**
 * Права на конкретном узле: ролевые плюс точечные из `node_permissions`
 * (ТЗ §10.3, §7.6).
 *
 * Объединение, а не замена: точечная запись только добавляет. Отнять ею
 * ничего нельзя намеренно — иначе «минус vnc:connect» пришлось бы искать
 * и учитывать в каждой проверке, а забытая запись тихо открывала бы доступ.
 *
 * Строки не из каталога §7.2 отбрасываются: право, которого не существует,
 * не должно попадать в набор даже из БД — иначе опечатка в таблице однажды
 * совпадёт с новым правом, которое мы добавим годом позже.
 */
export function withNodePermissions(rolePermissions, nodePermissions) {
    const result = new Set(rolePermissions);
    for (const permission of nodePermissions) {
        if (isPermission(permission)) {
            result.add(permission);
        }
    }
    return [...result];
}
/** Проверка, что пользователю доступны все перечисленные права. */
export function hasAllPermissions(granted, required) {
    return required.every((permission) => granted.includes(permission));
}
//# sourceMappingURL=permissions.js.map